import { useMemo } from "react";

import {
  DestinationPropertyFragment,
  SourcePropertyFragment,
} from "@/apollo/types";

import { DestinationPropertyOption } from "../../../state/StepStateContext";
import { PropertyMappingFormValue } from "../form";

export function getSourcePropertySets(
  sourceProperties: SourcePropertyFragment[],
  mappings: PropertyMappingFormValue[],
) {
  const mappingsWithSourceProperties = mappings.filter(
    (
      x,
    ): x is PropertyMappingFormValue & {
      sourceProperty: SourcePropertyFragment;
    } => x.sourceProperty != null,
  );

  const includedNames = new Set(
    mappingsWithSourceProperties.map((x) => x.sourceProperty.name),
  );

  const fullyMappedPropertyNames = new Set(
    mappingsWithSourceProperties
      .filter((x) => x.destinationProperty != null)
      .map((x) => x.sourceProperty.name),
  );

  const availableSourceProperties = sourceProperties.filter(
    (x) => !includedNames.has(x.name),
  );

  const unmappedSourceProperties = sourceProperties.filter(
    (x) => !fullyMappedPropertyNames.has(x.name),
  );

  return {
    all: sourceProperties,
    // All source properties that are unused
    available: availableSourceProperties,
    // All source properties that are unused or are not fully mapped to a destination property
    unmapped: unmappedSourceProperties,
  };
}

export function useSourcePropertySets(
  sourceProperties: SourcePropertyFragment[] | undefined,
  mappings: PropertyMappingFormValue[],
  primaryMapping?: PropertyMappingFormValue | null,
) {
  return useMemo(() => {
    return getSourcePropertySets(
      sourceProperties ?? [],
      mappings.concat(primaryMapping ?? []),
    );
  }, [sourceProperties, mappings, primaryMapping]);
}

export function getDestinationPropertySets(
  destinationProperties: DestinationPropertyFragment[],
  mappings: PropertyMappingFormValue[],
) {
  const mappingsWithDestinationProperties = mappings.filter(
    (
      x,
    ): x is PropertyMappingFormValue & {
      destinationProperty: DestinationPropertyOption;
    } => x.destinationProperty != null,
  );

  const includedIds = new Set(
    mappingsWithDestinationProperties.map((x) => x.destinationProperty.value),
  );

  const fullyMappedPropertyIds = new Set(
    mappingsWithDestinationProperties
      .filter((x) => x.sourceProperty != null)
      .map((x) => x.destinationProperty.value),
  );

  const availableDestinationProperties = destinationProperties.filter(
    (x) => !includedIds.has(x.propertyId),
  );

  const unmappedDestinationProperties = destinationProperties.filter(
    (x) => !fullyMappedPropertyIds.has(x.propertyId),
  );

  return {
    all: destinationProperties,
    allWritable: destinationProperties.filter((x) => !x.readonly),
    unmapped: unmappedDestinationProperties,
    unmappedWritable: unmappedDestinationProperties.filter((x) => !x.readonly),
    // All destination properties that are not yet added to mappings
    available: availableDestinationProperties,
    availableWritable: availableDestinationProperties.filter(
      (x) => !x.readonly,
    ),
    hasPrimaryKeyProperties: destinationProperties.some((x) => x.isPrimaryKey),
    required: destinationProperties.filter((x) => x.isRequired),
  };
}

export function useDestinationPropertySets(
  destinationProperties: DestinationPropertyFragment[] | undefined,
  mappings: PropertyMappingFormValue[],
  primaryMapping?: PropertyMappingFormValue | null,
) {
  return useMemo(() => {
    return getDestinationPropertySets(
      destinationProperties ?? [],
      mappings.concat(primaryMapping ?? []),
    );
  }, [destinationProperties, mappings, primaryMapping]);
}
